/**
 * ReadinessMeter (S1.3) — net-new shared component (holistic plan list).
 *
 * Visualizes the two-tier Product readiness gate (產品就緒 Product Ready) as a
 * per-group checklist + progress bar. Groups that still have gaps render as
 * clickable rows so the wizard Review step (and later the S1.4 detail page)
 * can click-scroll straight to the gap.
 *
 * Purely presentational — readiness itself is computed on READ
 * (`computeProductReadiness`, registry-driven); consumers pass the resolved
 * group states in. All user-visible text arrives localized via props
 * (WizardShell texts-prop precedent — common/ owns no namespace).
 */

import { Progress, Tag, Typography, theme } from 'antd';
import { CheckCircleFilled, ExclamationCircleFilled, RightOutlined } from '@ant-design/icons';
import { radiusTokens } from '@/styles/tokens';

const { Text } = Typography;

export interface ReadinessMeterItem {
  key: string;
  /** Localized group label, e.g. 描述（含報關品名）. */
  label: string;
  /** Optional localized detail line, e.g. "2 / 3". */
  detail?: string;
  complete: boolean;
  /** Present ⇒ the row is clickable (click-scrolls to the gap). */
  onClick?: () => void;
}

export interface ReadinessMeterProps {
  /** Localized meter title, e.g. 產品就緒檢查. */
  title: string;
  /** Overall readiness verdict — drives the status chip + bar color. */
  ready: boolean;
  /** Localized status chip text: 產品就緒 when ready, 未就緒 otherwise. */
  statusText: string;
  items: ReadinessMeterItem[];
  className?: string;
}

/**
 * Per-group readiness checklist with an overall progress bar.
 */
export const ReadinessMeter = ({
  title,
  ready,
  statusText,
  items,
  className,
}: ReadinessMeterProps) => {
  const { token } = theme.useToken();

  const completed = items.filter((item) => item.complete).length;
  const percent = items.length === 0 ? 100 : Math.round((completed / items.length) * 100);

  return (
    <div
      className={className}
      style={{
        backgroundColor: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        padding: token.paddingLG,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: token.marginSM,
        }}
      >
        <Text strong style={{ fontSize: token.fontSize }}>
          {title}
        </Text>
        <Tag color={ready ? 'success' : 'warning'} style={{ marginInlineEnd: 0 }}>
          {statusText}
        </Tag>
      </div>

      <Progress
        percent={percent}
        showInfo={false}
        strokeColor={ready ? token.colorSuccess : token.colorPrimary}
        trailColor={token.colorBgTextHover}
        size={{ height: 6 }}
      />

      <div style={{ marginTop: token.marginMD }}>
        {items.map((item) => {
          const clickable = item.onClick !== undefined;
          return (
            <div
              key={item.key}
              onClick={item.onClick}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onKeyDown={
                clickable
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        item.onClick?.();
                      }
                    }
                  : undefined
              }
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: token.marginSM,
                padding: `${token.paddingXS}px ${token.paddingSM}px`,
                borderRadius: radiusTokens.card,
                cursor: clickable ? 'pointer' : 'default',
                backgroundColor: item.complete ? 'transparent' : token.colorFillQuaternary,
                marginBottom: token.marginXXS,
              }}
            >
              {item.complete ? (
                <CheckCircleFilled
                  style={{ color: token.colorSuccess, fontSize: token.fontSize }}
                />
              ) : (
                <ExclamationCircleFilled
                  style={{ color: token.colorWarning, fontSize: token.fontSize }}
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text style={{ display: 'block' }}>{item.label}</Text>
                {item.detail !== undefined && (
                  <Text
                    style={{
                      display: 'block',
                      fontSize: token.fontSizeSM,
                      color: token.colorTextSecondary,
                    }}
                  >
                    {item.detail}
                  </Text>
                )}
              </div>
              {clickable && (
                <RightOutlined
                  style={{ color: token.colorTextSecondary, fontSize: token.fontSizeSM }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReadinessMeter;
